// Curated therapist directory used by /therapists and the Therapist Hub.
// Every listed therapist now has a dedicated illustration so the directory
// feels warm, premium, and consistent throughout.


export type SessionType = "Online" | "In-person" | "Phone" | "Group" | "Coaching" | "Workshop";
export type Availability = "Available now" | "This week" | "Taking new clients" | "Waitlist";

export type Therapist = {
  id: string;
  name: string;
  pronouns?: string;
  credentials: string;
  title: string;
  bio: string;
  location: string;
  country: string;
  yearsExperience: number;
  verified: boolean;
  contactEmail: string;
  modalities: string[]; // EMDR, IFS, Somatic, etc.
  specialties: string[];
  issues: string[]; // CPTSD, Anxiety, Flashbacks, etc.
  formats: SessionType[];
  availability: Availability[];
  languages: string[];
  feeRange: "$" | "$$" | "$$$" | "Sliding scale";
  acceptingClients: boolean;
  profileUrl: string;
  photo?: string;
  cartoonSeed: string;
  qualifications?: string[];
  professionalRegistrations?: { body: string; number?: string }[];
  memberships?: string[];
  sessionTypes?: string[];
  website?: string;
  verificationItems?: string[];
  serviceCategory?: ServiceCategory;
  licensingJurisdiction?: string;
  licensedStates?: string[];
  licensingNote?: string;
  internationalAvailability?: string;
  insuranceInfo?: string;
};

export type ServiceCategory =
  | "Psychotherapy"
  | "Counselling"
  | "Coaching"
  | "Consultation"
  | "Educational Services";


export const THERAPISTS: Therapist[] = [

];

export const ALL_MODALITIES = Array.from(
  new Set(THERAPISTS.flatMap((t) => t.modalities)),
).sort();
export const ALL_SPECIALTIES = Array.from(
  new Set(THERAPISTS.flatMap((t) => t.specialties)),
).sort();
export const ALL_ISSUES = [
  "CPTSD",
  "PTSD",
  "Anxiety",
  "Depression",
  "Flashbacks",
  "Dissociation",
  "Childhood Trauma",
  "Abuse Recovery",
  "Grief",
  "Burnout",
  "Stress",
  "Self-Worth",
  "Attachment Issues",
  "Relationships",
];
// Comprehensive country list for the global directory filter.
// "Anywhere / Online Worldwide" matches therapists offering online sessions
// regardless of their country of practice.
import { COUNTRY_OPTIONS } from "@/lib/therapist-licensing";

export const ANYWHERE_COUNTRY = "🌍 Anywhere / Online Worldwide";

export const ALL_COUNTRIES: string[] = [
  ANYWHERE_COUNTRY,
  ...COUNTRY_OPTIONS.map((c) => c.name),
];

// Common languages plus an "Other" catch-all so therapists who speak
// less-common languages still appear when "Other" is selected.
export const ALL_LANGUAGES: string[] = [
  "English",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Italian",
  "Dutch",
  "Polish",
  "Russian",
  "Ukrainian",
  "Arabic",
  "Hebrew",
  "Turkish",
  "Greek",
  "Swedish",
  "Norwegian",
  "Danish",
  "Finnish",
  "Mandarin",
  "Cantonese",
  "Japanese",
  "Korean",
  "Hindi",
  "Urdu",
  "Bengali",
  "Tagalog",
  "Vietnamese",
  "Thai",
  "Indonesian",
  "Malay",
  "Persian",
  "Romanian",
  "Czech",
  "Hungarian",
  "Bulgarian",
  "Other",
];

export const ALL_FORMATS: SessionType[] = [
  "Online",
  "In-person",
  "Phone",
  "Group",
  "Coaching",
  "Workshop",
];
export const ALL_AVAILABILITY: Availability[] = [
  "Available now",
  "This week",
  "Taking new clients",
];

// Map presenting issues → suggested therapy approaches. Used by the
// assessment results page to recommend modalities and matching therapists.
export const ISSUE_TO_APPROACHES: Record<string, string[]> = {
  CPTSD: ["IFS", "Somatic Experiencing", "EMDR", "Attachment-based"],
  PTSD: ["EMDR", "Somatic Experiencing", "Polyvagal"],
  Flashbacks: ["EMDR", "Somatic Experiencing", "IFS"],
  Dissociation: ["Somatic Experiencing", "Polyvagal", "IFS"],
  "Childhood Trauma": ["IFS", "Attachment-based", "EMDR"],
  "Abuse Recovery": ["EMDR", "IFS", "Somatic Experiencing"],
  Grief: ["Attachment-based", "Compassion-Focused", "EMDR"],
  Anxiety: ["CBT", "Polyvagal", "Compassion-Focused"],
  Depression: ["CBT", "IFS", "Compassion-Focused"],
  "Self-Worth": ["Compassion-Focused", "IFS"],
  "Attachment Issues": ["Attachment-based", "EFT", "IFS"],
  Relationships: ["Attachment-based", "Emotionally Focused Therapy"],
  Burnout: ["Polyvagal", "Somatic Experiencing"],
  Stress: ["Polyvagal", "Somatic Experiencing", "CBT"],
};
